import {
  EndpointItem,
  InitializerExecutor,
  RouteItem,
} from '@wajeez-web/core/types';
import { BiBookContent, BiCategory, BiSelectMultiple } from 'react-icons/bi';
import { FaLayerGroup, FaListUl, FaStackOverflow } from 'react-icons/fa';
import { MdOutlineCategory } from 'react-icons/md';
import { NavigationActions } from '@wajeez-web/stores/common';
import { HasPermission } from '@wajeez-web/components/helpers';
import {
  ContentEntityTypeActions,
  getContentState,
} from '@wajeez-web/stores/content';
import { store } from '@wajeez-web/stores/base';
import { getAuthState } from '@wajeez-web/stores/auth';
import { withTabs } from '@wajeez-web/core/utils';
import { GrDocumentConfig } from 'react-icons/gr';
import { VscTypeHierarchySub } from 'react-icons/vsc';

import ContentEntityQuotesPage from './Pages/ContentEntities/ContentEntityQuotesPage';
import CreateContentEntityPage from './Pages/ContentEntities/CreateContentEntityPage';
import ContentEntitiesPage from './Pages/ContentEntities/ContentEntitiesPage';
import AuthorsContentEntity from './Pages/AuthorsContentPage';
import ContentAnalysisPage from '../../../analysis/src/lib/pages/GeneralAnalysisPage';
import {
  AuthorCrump,
  ContentEntitiesCrump,
  ContentEntityCrump,
  FlowCrump,
  FlowCrumpV2,
} from './components/ContentBreadCrumps';
import DailyContentPage from './Pages/DailyContentPage';
import AuthorsPage from './Pages/AuthorsPage';
import NarratorsPage from './Pages/NarratorsPage';
import SuggestContent from './Pages/SuggestContentPage';
import FlowsPage from './Pages/FlowsPage';
import FlowsContentPage from './Pages/FlowsContentPage';
import { CreateAndEditFlowPage } from './Pages/CreateAndEditFlowPage';
import { CreateComponentPage } from './Pages/CreateComponentPage';
import { EditComponentPage } from './Pages/EditComponentPage';
import { CreateComponentTypePage } from './Pages/CreateComponentTypePage';
import FlowsV2Page from './Pages/FlowsV2Page';
import { EditCollectionComponentPage } from './Pages/EditCollectionComponentPage';
import { CreateAndEditFlowV2Page } from './Pages/CreateAndEditFlowV2Page';
import FlowsContentV2Page from './Pages/FlowsContentV2Page';
import { CreateComponentV2Page } from './Pages/CreateComponentV2Page';
import { CreateComponentTypeV2Page } from './Pages/CreateComponentTypeV2Page';
import { EditComponentV2Page } from './Pages/EditComponentV2Page';
import { EditCollectionComponentV2Page } from './Pages/EditCollectionComponentV2Page';
import FlowSelectionsPage from './Pages/FlowSelectionsPage';
import ComponentsPage from './Pages/ComponentsPage';
import { CreateComponentsComponentPage } from './Pages/CreateComponentsComponentPage';
import { CreateComponentsComponentTypePage } from './Pages/CreateComponentsComponentTypePage';
import { EditComponentsComponentPage } from './Pages/EditComponentsComponentPage';
import { RxComponent2 } from 'react-icons/rx';
import { MdContentPaste } from 'react-icons/md';
import ComponentTypesPage from './Pages/ComponentTypesPage';
import { CreateComponentTypesComponentTypePage } from './Pages/CreateComponentTypesComponentTypePage';
import CollectionComponentsPage from './Pages/CollectionComponentsPage';
import { EditCollectionComponentsCollectionComponentPage } from './Pages/EditCollectionComponentsCollectionComponentPage';
import { CreateCollectionComponentsCollectionComponentPage } from './Pages/CreateCollectionComponentsCollectionComponentPage';
import { CreateCollectionComponentTypePage } from './Pages/CreateCollectionComponentTypePage';
import CollectionComponentTypesPage from './Pages/CollectionComponentTypesPage';
import { CreateCollectionComponentTypesCollectionComponentTypePage } from './Pages/CreateCollectionComponentTypesCollectionComponentTypePage';
import { ComponentTypePage } from './Pages/ComponentTypePage';
import { CollectionComponentTypePage } from './Pages/CollectionComponentTypePage';
import { CreateCollectionComponentTypesCollectionComponentTypeWithPrefilledDataPage } from './Pages/CreateCollectionComponentTypesCollectionComponentTypeWithPrefilledDataPage';
import { CreateComponentTypesComponentTypeWithPrefilledDataPage } from './Pages/CreateComponentTypesComponentTypeWithPrefilledDataPage';
import { SystemConfigsPage } from './Pages/SystemConfigsPage';
import { ContentEntityTypeNavigationsPage } from './Pages/ContentEntityTypeNavigationsPage';
import { CreateAndEditContentEntityTypeNavigationPage } from './Pages/CreateAndEditContentEntityTypeNavigationPage';
import { ContentEntityListsPage } from './Pages/ContentEntityListsPage';
import { EditCollectionComponentTypesCollectionComponentTypePage } from './Pages/EditCollectionComponentTypesCollectionComponentTypePage';
import { EditComponentTypesComponentTypePage } from './Pages/EditComponentTypesComponentTypePage';
import ContentEntityTypesPage from './Pages/ContentEntityTypesPage';
import { CreateContentEntityTypePage } from './Pages/CreateContentEntityTypePage';
import { EditContentEntityTypePage } from './Pages/EditContentEntityTypePage';
import { CollectionComponentContentPage } from './Pages/CollectionComponentContentPage';
import { CreateCollectionComponentsComponentPage } from './Pages/CreateCollectionComponentsComponentPage';
import { CreateCollectionComponentsComponentTypePage } from './Pages/CreateCollectionComponentsComponentTypePage';
import { EditCollectionComponentsComponentPage } from './Pages/EditCollectionComponentsComponentPage';
import PublishersPage from './Pages/PublishersPage';
import VoiceActorsPage from './Pages/VoiceActorsPage';

export const initializeContentLookup: InitializerExecutor = async (
  dispatch
) => {
  if (!isNullOrEmpty(getAuthState(store).token) && HasPermission('36.0'))
    await Promise.all([
      dispatch(
        ContentEntityTypeActions.getAllContentEntityTypesAsync({
          page: 1,
          pageSize: 1000,
        })
      ),
      dispatch(ContentEntityTypeActions.getContentEntityTypeLookupsAsync()),
    ]);
};

export const fixedContentRoutes: RouteItem[] = [
  {
    name: 'Authors',
    href: '/authors',
    per: ['31.0'],
  },
  {
    name: 'Voice Actors',
    href: '/voiceActors',
    per: ['155.0'],
  },
  {
    name: 'Publishers',
    href: '/publishers',
    per: ['152.0'],
  },
  {
    name: 'Free Content',
    href: '/free-content',
    per: ['83.2'],
  },
  {
    name: 'Suggest Content',
    href: '/suggest-content',
    per: ['80.0'],
  },
  {
    name: 'Narrators',
    href: '/narrators',
    per: ['68.0'],
  },
];

export const initializeContentNavigation: InitializerExecutor = async (
  dispatch
) => {
  const contentEntities = getContentState(store).contentEntityType.lookups.map(
    (s) => s
  );

  const orderedContentEntities = contentEntities
    .filter((s) => s.shownInSideBar)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((x) => ({
      name: x.displayNamePlural ?? '',
      href: '/content/type/' + x.name,
      searchParams: '?page=1&pageSize=10',
      per: x.permission ? [x.permission] : [],
    }));

  dispatch(
    NavigationActions.updateRoutes([
      {
        name: 'Content',
        href: '/content',
        per: ['83.2', '68.0', '31.0', '80.0'],
        icon: BiBookContent,
        children: [
          ...orderedContentEntities.slice(0, 5),
          ...fixedContentRoutes.slice(0, 5),
          ...orderedContentEntities.slice(5, 14),
          ...fixedContentRoutes.slice(5),
          ...orderedContentEntities.slice(14),
        ],
      },
    ])
  );
};

export const initializeContentNavigationSuperAdmin: InitializerExecutor =
  async (dispatch) => {
    dispatch(
      NavigationActions.updateRoutes([
        {
          name: 'Flows',
          href: '/flows',
          per: ['101.7'],
          icon: FaStackOverflow,
          exclude: ['/flows/v2'],
        },
        {
          name: 'Flows V2',
          href: '/flows/v2',
          per: ['101.7'],
          icon: FaStackOverflow,
        },
        {
          name: 'Flow Selections',
          href: '/flow-selections',
          per: ['102.2'],
          icon: BiSelectMultiple,
        },
        {
          name: 'Components',
          href: '/components',
          per: ['51.10'],
          icon: RxComponent2,
          exclude: ['/flows', '/collection-components'],
        },
        {
          name: 'Component Types',
          href: '/component-types',
          per: ['52.13'],
          icon: MdOutlineCategory,
        },
        {
          name: 'Collection Components',
          href: '/collection-components',
          per: ['54.11'],
          icon: FaLayerGroup,
        },
        {
          name: 'Collection Component Types',
          href: '/collection-component-types',
          per: ['55.11'],
          icon: BiCategory,
        },
        {
          name: 'Content Entity Types',
          href: '/content-entity-types',
          per: ['36.0'],
          icon: MdContentPaste,
        },
        {
          name: 'System Configs',
          href: '/system-configs',
          per: ['144.0'],
          icon: GrDocumentConfig,
        },
        {
          name: 'Type Navigations',
          href: '/type-navigations',
          per: ['103.4'],
          icon: VscTypeHierarchySub,
        },
        {
          name: 'Content Entity Lists',
          href: '/content-entity-lists',
          per: ['145.0'],
          icon: FaListUl,
        },
      ])
    );
  };

function getContentRoutes(length: number, start = true): EndpointItem {
  return {
    type: 'container',
    endpoint: 'type/:type',
    crump: start ? ContentEntitiesCrump : undefined,
    per: [],
    children: [
      withTabs({
        type: 'endpoint',
        endpoint: 'create',
        crump: 'new',
        component: CreateContentEntityPage,
        per: [],
      }),
      {
        type: 'endpoint',
        endpoint: ':id/quotes',
        crump: ContentEntityCrump,
        component: ContentEntityQuotesPage,
        per: [],
      },
      length === 0
        ? withTabs({
            type: 'container',
            endpoint: ':id',
            crump: ContentEntityCrump,
            per: [],
            children: [
              withTabs({
                type: 'endpoint',
                endpoint: 'create',
                component: CreateContentEntityPage,
                per: [],
              }),

              {
                type: 'endpoint',
                endpoint: '',
                component: CreateContentEntityPage,
                per: [],
              },
            ],
          })
        : withTabs({
            type: 'container',
            endpoint: ':id',
            crump: ContentEntityCrump,
            per: [],
            children: [
              getContentRoutes(length - 1, false),
              withTabs({
                type: 'endpoint',
                endpoint: 'create',
                component: CreateContentEntityPage,
                per: [],
              }),
              // {
              //   type: 'endpoint',
              //   endpoint: 'quotes',
              //   component: ContentEntityQuotesPage,
              //   per: [],
              // },
              {
                type: 'endpoint',
                endpoint: '',
                component: CreateContentEntityPage,
                per: [],
              },
            ],
          }),
      {
        type: 'endpoint',
        endpoint: '',
        component: ContentEntitiesPage,
        per: [],
      },
    ],
  };
}

export const initializeContentEndpoints: InitializerExecutor = async (
  dispatch
) => {
  dispatch(
    NavigationActions.updateEndpoints([
      {
        type: 'container',
        endpoint: 'authors',
        per: ['31.0'],
        crump: 'Authors',
        children: [
          {
            type: 'endpoint',
            endpoint: '',
            component: AuthorsPage,
            per: [],
          },
          {
            type: 'container',
            endpoint: ':id/content',
            per: [],
            crump: AuthorCrump,
            children: [
              {
                type: 'endpoint',
                endpoint: '',
                component: AuthorsContentEntity,
                per: [],
              },
              {
                type: 'container',
                endpoint: 'content',
                per: [],
                children: [getContentRoutes(5, false)],
              },
            ],
          },
        ],
      },
      {
        type: 'container',
        endpoint: 'narrators',
        per: ['68.0'],
        crump: 'Narrators',
        children: [
          {
            type: 'endpoint',
            endpoint: '',
            component: NarratorsPage,
            per: [],
          },
        ],
      },
      {
        type: 'container',
        endpoint: 'content-analysis',
        per: ['120.8', '120.0'],
        crump: 'content-analysis',
        children: [
          {
            type: 'endpoint',
            endpoint: '',
            component: ContentAnalysisPage,
            per: [],
          },
        ],
      },
      {
        type: 'container',
        endpoint: 'free-content',
        per: ['83.2'],
        crump: 'Free Content',
        children: [
          {
            type: 'endpoint',
            endpoint: '',
            component: DailyContentPage,
            per: [],
          },
        ],
      },
      {
        type: 'container',
        endpoint: 'publishers',
        per: ['153.0'],
        crump: 'Publishers',
        children: [
          {
            type: 'endpoint',
            endpoint: '',
            component: PublishersPage,
            per: [],
          },
        ],
      },
      {
        type: 'container',
        endpoint: 'voiceActors',
        per: ['155.0'],
        crump: 'Voice Actors',
        children: [
          {
            type: 'endpoint',
            endpoint: '',
            component: VoiceActorsPage,
            per: [],
          },
        ],
      },
      {
        type: 'container',
        endpoint: 'content',
        per: ['27.26'],
        children: [getContentRoutes(5)],
      },
      {
        type: 'endpoint',
        endpoint: 'suggest-content',
        crump: 'Suggested Content',
        component: SuggestContent,
        per: ['80.0'],
      },
    ])
  );
};

export const initializeContentEndpointsSuperAdmin: InitializerExecutor = async (
  dispatch
) => {
  dispatch(
    NavigationActions.updateEndpoints([
      {
        type: 'container',
        endpoint: 'flows/v2',
        per: ['101.7'],
        crump: 'Flows V2',
        children: [
          {
            type: 'endpoint',
            endpoint: '',
            component: FlowsV2Page,
            per: [],
          },
          {
            type: 'container',
            endpoint: ':flowId/pages/:pageId',
            per: [],
            crump: FlowCrumpV2,
            children: [
              {
                type: 'endpoint',
                endpoint: '',
                component: FlowsContentV2Page,
                per: [],
              },
              {
                type: 'endpoint',
                endpoint: 'edit',
                component: EditCollectionComponentV2Page,
                per: [],
              },
              {
                type: 'container',
                endpoint: 'components/new',
                per: [],
                crump: 'New Component',
                children: [
                  {
                    type: 'endpoint',
                    endpoint: '',
                    component: CreateComponentV2Page,
                    per: [],
                  },
                  {
                    type: 'endpoint',
                    endpoint: 'types/new',
                    crump: 'New Type',
                    component: CreateComponentTypeV2Page,
                    per: [],
                  },
                ],
              },
              {
                type: 'container',
                endpoint: 'components/:componentId/edit',
                per: [],
                crump: 'Edit',
                children: [
                  {
                    type: 'endpoint',
                    endpoint: '',
                    component: EditComponentV2Page,
                    per: [],
                  },
                ],
              },
            ],
          },
          {
            type: 'container',
            endpoint: 'new',
            per: [],
            crump: 'New',
            children: [
              {
                type: 'endpoint',
                endpoint: '',
                component: CreateAndEditFlowV2Page,
                per: [],
              },
            ],
          },
          {
            type: 'container',
            endpoint: ':id/edit',
            per: [],
            crump: 'Edit',
            children: [
              {
                type: 'endpoint',
                endpoint: '',
                component: CreateAndEditFlowV2Page,
                per: [],
              },
            ],
          },
        ],
      },
      {
        type: 'container',
        endpoint: 'flows',
        per: ['101.7'],
        crump: 'Flows',
        children: [
          {
            type: 'endpoint',
            endpoint: '',
            component: FlowsPage,
            per: [],
          },
          {
            type: 'container',
            endpoint: ':flowId/pages/:pageId',
            per: [],
            crump: FlowCrump,
            children: [
              {
                type: 'endpoint',
                endpoint: '',
                component: FlowsContentPage,
                per: [],
              },
              {
                type: 'endpoint',
                endpoint: 'edit',
                component: EditCollectionComponentPage,
                per: [],
              },
              {
                type: 'container',
                endpoint: 'components/new',
                per: [],
                crump: 'New Component',
                children: [
                  {
                    type: 'endpoint',
                    endpoint: '',
                    component: CreateComponentPage,
                    per: [],
                  },
                  {
                    type: 'endpoint',
                    endpoint: 'types/new',
                    crump: 'New Type',
                    component: CreateComponentTypePage,
                    per: [],
                  },
                ],
              },
              {
                type: 'container',
                endpoint: 'components/:componentId/edit',
                per: [],
                crump: 'Edit',
                children: [
                  {
                    type: 'endpoint',
                    endpoint: '',
                    component: EditComponentPage,
                    per: [],
                  },
                ],
              },
            ],
          },
          {
            type: 'container',
            endpoint: 'new',
            per: [],
            crump: 'New',
            children: [
              {
                type: 'endpoint',
                endpoint: '',
                component: CreateAndEditFlowPage,
                per: [],
              },
            ],
          },
          {
            type: 'container',
            endpoint: ':id/edit',
            per: [],
            crump: 'Edit',
            children: [
              {
                type: 'endpoint',
                endpoint: '',
                component: CreateAndEditFlowPage,
                per: [],
              },
            ],
          },
        ],
      },
      {
        type: 'container',
        endpoint: 'flow-selections',
        per: [],
        crump: 'Flow Selections',
        children: [
          {
            type: 'endpoint',
            endpoint: '',
            component: FlowSelectionsPage,
            per: [],
          },
        ],
      },
      {
        type: 'container',
        endpoint: 'components',
        per: [],
        crump: 'Components',
        children: [
          {
            type: 'endpoint',
            endpoint: '',
            component: ComponentsPage,
            per: [],
          },
          {
            type: 'container',
            endpoint: 'new',
            per: [],
            crump: 'New Component',
            children: [
              {
                type: 'endpoint',
                endpoint: '',
                component: CreateComponentsComponentPage,
                per: [],
              },
              {
                type: 'endpoint',
                endpoint: 'types/new',
                crump: 'New Type',
                component: CreateComponentsComponentTypePage,
                per: [],
              },
            ],
          },
          {
            type: 'container',
            endpoint: ':componentId/edit',
            per: [],
            crump: 'Edit',
            children: [
              {
                type: 'endpoint',
                endpoint: '',
                component: EditComponentsComponentPage,
                per: [],
              },
            ],
          },
        ],
      },
      {
        type: 'container',
        endpoint: 'component-types',
        per: [],
        crump: 'Component Types',
        children: [
          {
            type: 'endpoint',
            endpoint: '',
            component: ComponentTypesPage,
            per: [],
          },
          {
            type: 'container',
            endpoint: 'new',
            per: [],
            crump: 'New Component Type',
            children: [
              {
                type: 'endpoint',
                endpoint: '',
                component: CreateComponentTypesComponentTypePage,
                per: [],
              },
            ],
          },
          {
            type: 'endpoint',
            endpoint: ':id',
            component: ComponentTypePage,
            per: [],
          },
          {
            type: 'container',
            endpoint: ':id/new',
            per: [],
            crump: 'Create',
            children: [
              {
                type: 'endpoint',
                endpoint: '',
                component:
                  CreateComponentTypesComponentTypeWithPrefilledDataPage,
                per: [],
              },
            ],
          },
          {
            type: 'container',
            endpoint: ':id/edit',
            per: [],
            crump: 'Edit',
            children: [
              {
                type: 'endpoint',
                endpoint: '',
                component: EditComponentTypesComponentTypePage,
                per: [],
              },
            ],
          },
        ],
      },
      {
        type: 'container',
        endpoint: 'collection-components',
        per: [],
        crump: 'Collection Components',
        children: [
          {
            type: 'endpoint',
            endpoint: '',
            component: CollectionComponentsPage,
            per: [],
          },
          {
            type: 'container',
            endpoint: 'new',
            per: [],
            crump: 'New Component',
            children: [
              {
                type: 'endpoint',
                endpoint: '',
                component: CreateCollectionComponentsCollectionComponentPage,
                per: [],
              },
              {
                type: 'endpoint',
                endpoint: 'types/new',
                crump: 'New Type',
                component: CreateCollectionComponentTypePage,
                per: [],
              },
            ],
          },
          {
            type: 'container',
            endpoint: ':pageId',
            per: [],
            children: [
              {
                type: 'endpoint',
                endpoint: '',
                component: CollectionComponentContentPage,
                per: [],
              },
              {
                type: 'container',
                endpoint: 'components/new',
                per: [],
                crump: 'New Component',
                children: [
                  {
                    type: 'endpoint',
                    endpoint: '',
                    component: CreateCollectionComponentsComponentPage,
                    per: [],
                  },
                  {
                    type: 'endpoint',
                    endpoint: 'types/new',
                    crump: 'New Type',
                    component: CreateCollectionComponentsComponentTypePage,
                    per: [],
                  },
                ],
              },
              {
                type: 'container',
                endpoint: 'components/:componentId/edit',
                per: [],
                crump: 'Edit',
                children: [
                  {
                    type: 'endpoint',
                    endpoint: '',
                    component: EditCollectionComponentsComponentPage,
                    per: [],
                  },
                ],
              },
            ],
          },
          {
            type: 'endpoint',
            endpoint: ':id/edit',
            component: EditCollectionComponentsCollectionComponentPage,
            crump: 'Edit',
            per: [],
          },
        ],
      },
      {
        type: 'container',
        endpoint: 'collection-component-types',
        per: [],
        crump: 'Collection Component Types',
        children: [
          {
            type: 'endpoint',
            endpoint: '',
            component: CollectionComponentTypesPage,
            per: [],
          },
          {
            type: 'container',
            endpoint: 'new',
            per: [],
            crump: 'New Type',
            children: [
              {
                type: 'endpoint',
                endpoint: '',
                component:
                  CreateCollectionComponentTypesCollectionComponentTypePage,
                per: [],
              },
            ],
          },
          {
            type: 'endpoint',
            endpoint: ':id',
            component: CollectionComponentTypePage,
            per: [],
          },
          {
            type: 'container',
            endpoint: ':id/new',
            per: [],
            crump: 'Create',
            children: [
              {
                type: 'endpoint',
                endpoint: '',
                component:
                  CreateCollectionComponentTypesCollectionComponentTypeWithPrefilledDataPage,
                per: [],
              },
            ],
          },
          {
            type: 'container',
            endpoint: ':id/edit',
            per: [],
            crump: 'Edit',
            children: [
              {
                type: 'endpoint',
                endpoint: '',
                component:
                  EditCollectionComponentTypesCollectionComponentTypePage,
                per: [],
              },
            ],
          },
        ],
      },
      {
        type: 'container',
        endpoint: 'content-entity-types',
        per: [],
        crump: 'Content Entity Types',
        children: [
          {
            type: 'endpoint',
            endpoint: '',
            component: ContentEntityTypesPage,
            per: [],
          },
          {
            type: 'container',
            endpoint: 'new',
            per: [],
            crump: 'New Type',
            children: [
              {
                type: 'endpoint',
                endpoint: '',
                component: CreateContentEntityTypePage,
                per: [],
              },
            ],
          },
          {
            type: 'container',
            endpoint: ':id/edit',
            per: [],
            crump: 'Edit',
            children: [
              {
                type: 'endpoint',
                endpoint: '',
                component: EditContentEntityTypePage,
                per: [],
              },
            ],
          },
        ],
      },
      {
        type: 'container',
        endpoint: 'system-configs',
        per: [],
        crump: 'System Configs',
        children: [
          {
            type: 'endpoint',
            endpoint: '',
            component: SystemConfigsPage,
            per: [],
          },
        ],
      },
      {
        type: 'container',
        endpoint: 'type-navigations',
        per: [],
        crump: 'Type Navigations',
        children: [
          {
            type: 'endpoint',
            endpoint: '',
            component: ContentEntityTypeNavigationsPage,
            per: [],
          },
          {
            type: 'container',
            endpoint: 'new',
            per: [],
            crump: 'New',
            children: [
              {
                type: 'endpoint',
                endpoint: '',
                component: CreateAndEditContentEntityTypeNavigationPage,
                per: [],
              },
            ],
          },
          {
            type: 'container',
            endpoint: ':id/edit',
            per: [],
            crump: 'Edit',
            children: [
              {
                type: 'endpoint',
                endpoint: '',
                component: CreateAndEditContentEntityTypeNavigationPage,
                per: [],
              },
            ],
          },
        ],
      },
      {
        type: 'container',
        endpoint: 'content-entity-lists',
        per: [],
        crump: 'Content Entity Lists',
        children: [
          {
            type: 'endpoint',
            endpoint: '',
            component: ContentEntityListsPage,
            per: [],
          },
        ],
      },
    ])
  );
};

/// check if the string is null or undefined or empty
export function isNullOrEmpty(str: string | null | undefined): boolean {
  return str === null || str === undefined || str === '';
}
